import { useEffect, useState, useCallback } from 'react'
import { lumi } from '@/lib/lumi'
import { isValidUsername } from '@/lib/auth-helpers'
import {Plus, Trash2, Loader2, X, ShieldCheck, User, Pencil, Eye, FilePen, Eraser} from 'lucide-react'
import toast from 'react-hot-toast'
import type { UserProfile, UserModuleAccess, ModulePermission, ModulePermissions } from '@/types'
import { ALL_USER_MODULES, USER_MODULE_LABELS } from '@/types'

const TEAL    = '#5BBDB5'
const TEAL_BG = '#DCF0EE'
const GOLD    = '#C8922A'

const PERMISSION_LABELS: Record<string, string> = {
  admin: 'Administrador',
  user:  'Utilizador',
}

const DEFAULT_MOD_PERM: ModulePermission = { view: false, edit: false, delete: false }

const emptyForm = {
  displayName:       '',
  username:          '',
  password:          '',
  confirmPassword:   '',
  permission:        'user' as UserProfile['permission'],
  modulePermissions: {} as ModulePermissions,
}

const emptyEditForm = {
  displayName:       '',
  permission:        'user' as UserProfile['permission'],
  modulePermissions: {} as ModulePermissions,
  password:          '',
  confirmPassword:   '',
}

// ── Helpers ────────────────────────────────────────────────────────────────────
function getModPerm(mp: ModulePermissions, mod: UserModuleAccess): ModulePermission {
  return mp[mod] ?? { ...DEFAULT_MOD_PERM }
}

function hasAnyAccess(mp: ModulePermissions, mod: UserModuleAccess): boolean {
  const p = mp[mod]
  return !!(p?.view || p?.edit || p?.delete)
}

// Mantém retrocompatibilidade: devolve array de módulos com pelo menos view=true
function modulesFromPermissions(mp: ModulePermissions): UserModuleAccess[] {
  return ALL_USER_MODULES.filter(m => hasAnyAccess(mp, m))
}

// ── Sub-componente: linha de permissões por módulo ─────────────────────────────
interface ModuleRowProps {
  mod: UserModuleAccess
  perm: ModulePermission
  onChange: (mod: UserModuleAccess, key: keyof ModulePermission, val: boolean) => void
}

function ModuleRow({ mod, perm, onChange }: ModuleRowProps) {
  const isOn = perm.view || perm.edit || perm.delete

  const ACTION_ICONS = [
    { key: 'view'   as const, Icon: Eye,     label: 'Ver'      },
    { key: 'edit'   as const, Icon: FilePen, label: 'Editar'   },
    { key: 'delete' as const, Icon: Eraser,  label: 'Eliminar' },
  ]

  return (
    <div
      className="rounded-xl border p-3 transition-all"
      style={{ borderColor: isOn ? TEAL : '#e5e7eb', background: isOn ? TEAL_BG : '#fafafa' }}
    >
      {/* Nome do módulo */}
      <p className="text-xs font-bold text-gray-700 dark:text-gray-200 mb-2 truncate">
        {USER_MODULE_LABELS[mod]}
      </p>

      {/* Checkboxes: Ver / Editar / Eliminar */}
      <div className="flex items-center gap-2">
        {ACTION_ICONS.map(({ key, Icon, label }) => {
          const checked = perm[key]
          return (
            <label
              key={key}
              className="flex-1 flex flex-col items-center gap-1 cursor-pointer select-none p-1.5 rounded-lg transition-all"
              style={{
                background: checked ? TEAL : '#f3f4f6',
                color:      checked ? '#fff' : '#9ca3af',
              }}
              title={label}
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={checked}
                onChange={e => {
                  // Se activar Editar ou Eliminar, activa View automaticamente
                  if ((key === 'edit' || key === 'delete') && e.target.checked && !perm.view) {
                    onChange(mod, 'view', true)
                  }
                  onChange(mod, key, e.target.checked)
                }}
              />
              <Icon size={13} />
              <span className="text-[10px] font-semibold leading-none">{label}</span>
            </label>
          )
        })}
      </div>
    </div>
  )
}

// ── Componente principal ───────────────────────────────────────────────────────
export default function ManageUsers() {
  const [users,   setUsers]   = useState<UserProfile[]>([])
  const [loading, setLoading] = useState(true)

  const [showForm, setShowForm] = useState(false)
  const [form,     setForm]     = useState(emptyForm)
  const [saving,   setSaving]   = useState(false)
  const [errors,   setErrors]   = useState<Record<string, string>>({})

  const [editTarget, setEditTarget] = useState<UserProfile | null>(null)
  const [editForm,   setEditForm]   = useState(emptyEditForm)
  const [editSaving, setEditSaving] = useState(false)
  const [editErrors, setEditErrors] = useState<Record<string, string>>({})

  const [confirmDelete, setConfirmDelete] = useState<UserProfile | null>(null)
  const [deleting,      setDeleting]      = useState<string | null>(null)

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.user_profiles.list({
        sort: { createdAt: -1 },
        limit: 100,
      })
      setUsers(list as UserProfile[])
    } catch {
      toast.error('Erro ao carregar utilizadores.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchUsers() }, [fetchUsers])

  // ── Mudar permissão de módulo ──────────────────────────────────────────────
  const setModPerm = (
    setter: React.Dispatch<React.SetStateAction<typeof emptyForm>>,
    mod: UserModuleAccess,
    key: keyof ModulePermission,
    val: boolean,
  ) => {
    setter(f => ({
      ...f,
      modulePermissions: {
        ...f.modulePermissions,
        [mod]: { ...getModPerm(f.modulePermissions, mod), [key]: val },
      },
    }))
  }

  const setEditModPerm = (mod: UserModuleAccess, key: keyof ModulePermission, val: boolean) => {
    setEditForm(f => ({
      ...f,
      modulePermissions: {
        ...f.modulePermissions,
        [mod]: { ...getModPerm(f.modulePermissions, mod), [key]: val },
      },
    }))
  }

  // ── Validações ─────────────────────────────────────────────────────────────
  const validate = (): boolean => {
    const errs: Record<string, string> = {}
    if (!form.displayName.trim())    errs.displayName    = 'O nome é obrigatório.'
    if (!form.username.trim())       errs.username       = 'O nome de utilizador é obrigatório.'
    else if (!isValidUsername(form.username)) errs.username = 'Só letras, números, pontos, hífens e underscores (3–32 caracteres).'
    if (!form.password)              errs.password       = 'A senha é obrigatória.'
    else if (form.password.length < 6) errs.password     = 'A senha deve ter pelo menos 6 caracteres.'
    if (form.confirmPassword !== form.password) errs.confirmPassword = 'As senhas não coincidem.'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const validateEdit = (): boolean => {
    const errs: Record<string, string> = {}
    if (!editForm.displayName.trim()) errs.displayName = 'O nome é obrigatório.'
    if (editForm.password && editForm.password.length < 6) errs.password = 'A senha deve ter pelo menos 6 caracteres.'
    if (editForm.password && editForm.confirmPassword !== editForm.password) errs.confirmPassword = 'As senhas não coincidem.'
    setEditErrors(errs)
    return Object.keys(errs).length === 0
  }

  // ── Criar utilizador ───────────────────────────────────────────────────────
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    setSaving(true)
    try {
      const mp      = form.permission === 'user' ? form.modulePermissions : {}
      const modules = form.permission === 'user' ? modulesFromPermissions(mp) : []

      const result = await lumi.functions.invoke('createAdminUser', {
        method: 'POST',
        body: {
          action:            'create_profile',
          username:          form.username.trim(),
          password:          form.password,
          displayName:       form.displayName.trim(),
          permission:        form.permission,
          modules,
          modulePermissions: mp,
          email:             `${form.username.toLowerCase().trim()}@rcf.internal`,
        },
      }) as any

      if (result?.error || result?.success === false) throw new Error(result?.error)
      toast.success(`Utilizador "${form.displayName}" criado com sucesso!`)
      setForm(emptyForm)
      setErrors({})
      setShowForm(false)
      fetchUsers()
    } catch (err: any) {
      toast.error(err?.data?.error || err?.message || 'Erro ao criar utilizador.')
    } finally {
      setSaving(false)
    }
  }

  // ── Abrir edição ───────────────────────────────────────────────────────────
  const openEdit = (u: UserProfile) => {
    // Reconstruir modulePermissions a partir do campo guardado ou dos módulos legados
    let mp: ModulePermissions = u.modulePermissions || {}
    if (Object.keys(mp).length === 0 && u.modules && u.modules.length > 0) {
      // Migrar formato antigo: módulo listado = view:true
      mp = Object.fromEntries(u.modules.map(m => [m, { view: true, edit: false, delete: false }]))
    }
    setEditTarget(u)
    setEditForm({
      displayName:       u.displayName || '',
      permission:        u.permission === 'admin' ? 'admin' : 'user',
      modulePermissions: mp,
      password:          '',
      confirmPassword:   '',
    })
    setEditErrors({})
  }

  // ── Guardar edição ─────────────────────────────────────────────────────────
  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editTarget || !validateEdit()) return
    setEditSaving(true)
    try {
      const mp      = editForm.permission === 'user' ? editForm.modulePermissions : {}
      const modules = editForm.permission === 'user' ? modulesFromPermissions(mp) : []

      const result = await lumi.functions.invoke('createAdminUser', {
        method: 'POST',
        body: {
          action:            'update_profile',
          profileId:         editTarget._id,
          displayName:       editForm.displayName.trim(),
          permission:        editForm.permission,
          modules,
          modulePermissions: mp,
          ...(editForm.password ? { password: editForm.password } : {}),
        },
      }) as any

      if (result?.error || result?.success === false) throw new Error(result?.error)
      toast.success('Utilizador actualizado com sucesso!')
      setEditTarget(null)
      fetchUsers()
    } catch (err: any) {
      toast.error(err?.data?.error || err?.message || 'Erro ao actualizar utilizador.')
    } finally {
      setEditSaving(false)
    }
  }

  // ── Eliminar ───────────────────────────────────────────────────────────────
  const handleDelete = async (profile: UserProfile) => {
    setDeleting(profile._id)
    try {
      await lumi.entities.user_profiles.delete(profile._id)
      toast.success('Perfil eliminado com sucesso.')
      setUsers(prev => prev.filter(u => u._id !== profile._id))
    } catch {
      toast.error('Erro ao eliminar perfil.')
    } finally {
      setDeleting(null)
      setConfirmDelete(null)
    }
  }

  // ── Resumo de permissões (para a lista) ────────────────────────────────────
  const renderPermSummary = (u: UserProfile) => {
    const mp: ModulePermissions = u.modulePermissions || {}
    const active = ALL_USER_MODULES.filter(m => hasAnyAccess(mp, m))
    if (active.length === 0) {
      // fallback legado
      if (u.modules && u.modules.length > 0) {
        return (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {u.modules.map(mod => (
              <span key={mod} className="text-xs px-1.5 py-0.5 rounded font-medium" style={{ background: TEAL_BG, color: TEAL }}>
                {USER_MODULE_LABELS[mod] || mod}
              </span>
            ))}
          </div>
        )
      }
      return <span className="text-xs text-gray-400 mt-1 block">Sem módulos atribuídos</span>
    }
    return (
      <div className="flex flex-wrap gap-1 mt-1.5">
        {active.map(mod => {
          const p = mp[mod]!
          const icons = [
            p.view   && <Eye     key="v" size={9} />,
            p.edit   && <FilePen key="e" size={9} />,
            p.delete && <Eraser  key="d" size={9} />,
          ].filter(Boolean)
          return (
            <span
              key={mod}
              className="flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded font-medium"
              style={{ background: TEAL_BG, color: TEAL }}
              title={`${USER_MODULE_LABELS[mod]}: ${[p.view && 'Ver', p.edit && 'Editar', p.delete && 'Eliminar'].filter(Boolean).join(', ')}`}
            >
              {icons}
              <span className="ml-0.5">{USER_MODULE_LABELS[mod]}</span>
            </span>
          )
        })}
      </div>
    )
  }

  // ── JSX principal ──────────────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Utilizadores</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {users.length} utilizador(es) cadastrado(s)
          </p>
        </div>
        <button
          onClick={() => { setForm(emptyForm); setErrors({}); setShowForm(true) }}
          className="flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg transition-colors"
          style={{ background: TEAL }}
          onMouseEnter={e => (e.currentTarget.style.background = '#4aaba3')}
          onMouseLeave={e => (e.currentTarget.style.background = TEAL)}
        >
          <Plus size={16} />
          Novo Utilizador
        </button>
      </div>

      {/* Lista */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 size={24} className="animate-spin" style={{ color: TEAL }} />
          </div>
        ) : users.length === 0 ? (
          <div className="py-16 text-center">
            <User size={32} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-sm text-gray-400">Nenhum utilizador cadastrado ainda.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50 dark:divide-gray-800">
            {users.map(u => (
              <div key={u._id} className="flex items-start gap-4 px-5 py-4">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white text-sm mt-0.5"
                  style={{ background: u.permission === 'admin' ? TEAL : '#6b7280' }}
                >
                  {u.displayName?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {u.displayName}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5 truncate">
                    @{(u as any).username || '—'}
                  </p>
                  {u.permission === 'user' && renderPermSummary(u)}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 mt-0.5">
                  <span
                    className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium"
                    style={
                      u.permission === 'admin'
                        ? { background: TEAL_BG, color: '#1e7a74' }
                        : { background: '#f3f4f6', color: '#4b5563' }
                    }
                  >
                    <ShieldCheck size={11} />
                    {PERMISSION_LABELS[u.permission] || u.permission}
                  </span>
                  <button
                    onClick={() => openEdit(u)}
                    className="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950 transition-all"
                    title="Editar"
                  >
                    <Pencil size={15} />
                  </button>
                  <button
                    onClick={() => setConfirmDelete(u)}
                    disabled={deleting === u._id}
                    className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950 transition-all"
                    title="Eliminar"
                  >
                    {deleting === u._id
                      ? <Loader2 size={15} className="animate-spin" />
                      : <Trash2 size={15} />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Modal: Novo Utilizador ─────────────────────────────────────────── */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800 p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Novo Utilizador</h3>
              <button onClick={() => setShowForm(false)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleSave} className="space-y-4" noValidate>
              {/* Nome */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Nome <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.displayName}
                  onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
                  placeholder="Ex: Carlos Felipe"
                  autoFocus
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none transition-colors"
                  onFocus={e => (e.currentTarget.style.boxShadow = `0 0 0 2px ${TEAL}40`)}
                  onBlur={e => (e.currentTarget.style.boxShadow = '')}
                />
                {errors.displayName && <p className="text-xs text-red-500 mt-1">{errors.displayName}</p>}
              </div>
              {/* Username */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Utilizador (Login) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.username}
                  onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                  placeholder="Ex: carlos.felipe"
                  spellCheck={false}
                  autoComplete="off"
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none transition-colors"
                  onFocus={e => (e.currentTarget.style.boxShadow = `0 0 0 2px ${TEAL}40`)}
                  onBlur={e => (e.currentTarget.style.boxShadow = '')}
                />
                {errors.username
                  ? <p className="text-xs text-red-500 mt-1">{errors.username}</p>
                  : <p className="text-xs text-gray-400 mt-1">Letras, números, pontos, hífens ou underscores.</p>}
              </div>
              {/* Senha */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Senha <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    placeholder="Mín. 6 caracteres"
                    autoComplete="new-password"
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none transition-colors"
                    onFocus={e => (e.currentTarget.style.boxShadow = `0 0 0 2px ${TEAL}40`)}
                    onBlur={e => (e.currentTarget.style.boxShadow = '')}
                  />
                  {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Confirmar Senha <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={form.confirmPassword}
                    onChange={e => setForm(f => ({ ...f, confirmPassword: e.target.value }))}
                    placeholder="Repita a senha"
                    autoComplete="new-password"
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none transition-colors"
                    onFocus={e => (e.currentTarget.style.boxShadow = `0 0 0 2px ${TEAL}40`)}
                    onBlur={e => (e.currentTarget.style.boxShadow = '')}
                  />
                  {errors.confirmPassword && <p className="text-xs text-red-500 mt-1">{errors.confirmPassword}</p>}
                </div>
              </div>
              {/* Tipo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tipo de Conta</label>
                <div className="grid grid-cols-2 gap-2">
                  {(['admin', 'user'] as const).map(p => (
                    <label
                      key={p}
                      className="flex items-center gap-2.5 p-3 rounded-lg border cursor-pointer transition-all"
                      style={{
                        borderColor: form.permission === p ? TEAL : '#e5e7eb',
                        background:  form.permission === p ? TEAL_BG : 'transparent',
                      }}
                    >
                      <input
                        type="radio"
                        name="permission_new"
                        value={p}
                        checked={form.permission === p}
                        onChange={() => setForm(f => ({ ...f, permission: p, modulePermissions: {} }))}
                        className="accent-teal-500"
                      />
                      <div>
                        <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">{PERMISSION_LABELS[p]}</div>
                        <div className="text-xs text-gray-400">{p === 'admin' ? 'Acesso total' : 'Acesso por módulos'}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Permissões granulares (só Utilizador) */}
              {form.permission === 'user' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Permissões por Módulo
                    </label>
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <Eye size={10} /> Ver &nbsp;
                      <FilePen size={10} /> Editar &nbsp;
                      <Eraser size={10} /> Eliminar
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {ALL_USER_MODULES.map(mod => (
                      <ModuleRow
                        key={mod}
                        mod={mod}
                        perm={getModPerm(form.modulePermissions, mod)}
                        onChange={(m, k, v) => setModPerm(setForm as any, m, k, v)}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                    <span style={{ color: GOLD }}>●</span>
                    Activar "Editar" ou "Eliminar" activa "Ver" automaticamente.
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 py-2.5 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-2.5 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ background: TEAL }}
                >
                  {saving ? <Loader2 size={15} className="animate-spin" /> : null}
                  Criar Utilizador
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Editar Utilizador ───────────────────────────────────────── */}
      {editTarget && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800 p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Editar Utilizador</h3>
                <p className="text-xs text-gray-400 mt-0.5">@{(editTarget as any).username}</p>
              </div>
              <button onClick={() => setEditTarget(null)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleEditSave} className="space-y-4" noValidate>
              {/* Nome */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Nome <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editForm.displayName}
                  onChange={e => setEditForm(f => ({ ...f, displayName: e.target.value }))}
                  placeholder="Nome do utilizador"
                  autoFocus
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none transition-colors"
                  onFocus={e => (e.currentTarget.style.boxShadow = `0 0 0 2px ${TEAL}40`)}
                  onBlur={e => (e.currentTarget.style.boxShadow = '')}
                />
                {editErrors.displayName && <p className="text-xs text-red-500 mt-1">{editErrors.displayName}</p>}
              </div>
              {/* Tipo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tipo de Conta</label>
                <div className="grid grid-cols-2 gap-2">
                  {(['admin', 'user'] as const).map(p => (
                    <label
                      key={p}
                      className="flex items-center gap-2.5 p-3 rounded-lg border cursor-pointer transition-all"
                      style={{
                        borderColor: editForm.permission === p ? TEAL : '#e5e7eb',
                        background:  editForm.permission === p ? TEAL_BG : 'transparent',
                      }}
                    >
                      <input
                        type="radio"
                        name="permission_edit"
                        value={p}
                        checked={editForm.permission === p}
                        onChange={() => setEditForm(f => ({ ...f, permission: p, modulePermissions: {} }))}
                        className="accent-teal-500"
                      />
                      <div>
                        <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">{PERMISSION_LABELS[p]}</div>
                        <div className="text-xs text-gray-400">{p === 'admin' ? 'Acesso total' : 'Acesso por módulos'}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Permissões granulares */}
              {editForm.permission === 'user' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Permissões por Módulo
                    </label>
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <Eye size={10} /> Ver &nbsp;
                      <FilePen size={10} /> Editar &nbsp;
                      <Eraser size={10} /> Eliminar
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {ALL_USER_MODULES.map(mod => (
                      <ModuleRow
                        key={mod}
                        mod={mod}
                        perm={getModPerm(editForm.modulePermissions, mod)}
                        onChange={setEditModPerm}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                    <span style={{ color: GOLD }}>●</span>
                    Activar "Editar" ou "Eliminar" activa "Ver" automaticamente.
                  </p>
                </div>
              )}

              {/* Nova senha */}
              <div className="border-t border-gray-100 dark:border-gray-800 pt-4">
                <p className="text-xs text-gray-400 mb-3">Deixe em branco para manter a senha actual.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Nova Senha</label>
                    <input
                      type="password"
                      value={editForm.password}
                      onChange={e => setEditForm(f => ({ ...f, password: e.target.value }))}
                      placeholder="Mín. 6 caracteres"
                      autoComplete="new-password"
                      className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none transition-colors"
                      onFocus={e => (e.currentTarget.style.boxShadow = `0 0 0 2px ${TEAL}40`)}
                      onBlur={e => (e.currentTarget.style.boxShadow = '')}
                    />
                    {editErrors.password && <p className="text-xs text-red-500 mt-1">{editErrors.password}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Confirmar Nova Senha</label>
                    <input
                      type="password"
                      value={editForm.confirmPassword}
                      onChange={e => setEditForm(f => ({ ...f, confirmPassword: e.target.value }))}
                      placeholder="Repita a senha"
                      autoComplete="new-password"
                      className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none transition-colors"
                      onFocus={e => (e.currentTarget.style.boxShadow = `0 0 0 2px ${TEAL}40`)}
                      onBlur={e => (e.currentTarget.style.boxShadow = '')}
                    />
                    {editErrors.confirmPassword && <p className="text-xs text-red-500 mt-1">{editErrors.confirmPassword}</p>}
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setEditTarget(null)}
                  className="flex-1 py-2.5 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={editSaving}
                  className="flex-1 py-2.5 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ background: TEAL }}
                >
                  {editSaving ? <Loader2 size={15} className="animate-spin" /> : <Pencil size={15} />}
                  Guardar Alterações
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Confirmar eliminação ────────────────────────────────────── */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800 p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Eliminar Perfil</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
              Deseja eliminar o perfil de{' '}
              <strong className="text-gray-700 dark:text-gray-300">"{confirmDelete.displayName}"</strong>?
              Esta acção é permanente.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 py-2.5 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                disabled={!!deleting}
                className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? <Loader2 size={15} className="animate-spin" /> : null}
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
